import { toPng } from 'html-to-image';

/** Single place for the formats of the dashboard image export: the type, the menu and the default derive from it. */
export const IMAGE_EXPORT_FORMATS = ['PNG', 'PDF'] as const;
export type ImageExportFormat = (typeof IMAGE_EXPORT_FORMATS)[number];
export const PDF_EXPORT_FORMAT = 'PDF' satisfies ImageExportFormat;
export const DEFAULT_IMAGE_EXPORT_FORMAT: ImageExportFormat = IMAGE_EXPORT_FORMATS[0];

export interface CapturedImage {
  /** Data URL of the PNG, used for the preview in the export dialog. */
  previewUrl: string;
  blob: Blob;
  /** Size in CSS pixels of the captured area. */
  width: number;
  height: number;
}

/** Elements with this attribute (e.g. the fullscreen button of a tile) are left out of the picture. */
export const EXPORT_IGNORE_ATTRIBUTE = 'data-export-ignore';

const CAPTURE_OPTIONS = {
  pixelRatio: 2,
  backgroundColor: '#ffffff',
  cacheBust: true,
  filter: (node: Node) => !(node instanceof Element && node.hasAttribute(EXPORT_IGNORE_ATTRIBUTE)),
};

/** Renders the given element (as currently displayed) into a PNG. */
export async function captureElementAsPng(element: HTMLElement): Promise<CapturedImage> {
  const restoreCharts = await replaceChartsWithImages(element);
  let previewUrl: string;
  try {
    previewUrl = await toPng(element, CAPTURE_OPTIONS);
  } finally {
    restoreCharts();
  }
  const blob = await (await fetch(previewUrl)).blob();
  return { previewUrl, blob, width: element.offsetWidth, height: element.offsetHeight };
}

export interface TileCaptureMeta {
  /** Line above the tile, e.g. "Bestandsanalyse | UIN-Fonds Nr. 618 | Relative Werte". */
  header: string;
  /** Line below the tile, e.g. "Stichtag: 28.11.2025". */
  footer: string;
}

/**
 * Captures a single tile as PNG and adds a context line above (analysis, fund, value mode) and below
 * (Stichtag). The lines are drawn onto a canvas around the captured tile, so the layout of the live
 * tile (fixed grid height) is not touched.
 */
export async function captureTileAsPng(element: HTMLElement, meta: TileCaptureMeta): Promise<CapturedImage> {
  const tile = await captureElementAsPng(element);
  const picture = new Image();
  picture.src = tile.previewUrl;
  await picture.decode();

  const ratio = CAPTURE_OPTIONS.pixelRatio;
  const padding = 16 * ratio;
  const lineHeight = 20 * ratio;
  const canvas = document.createElement('canvas');
  canvas.width = picture.width + 2 * padding;
  canvas.height = picture.height + 2 * padding + 2 * lineHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas not available');
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = '#525252';
  context.font = `${12 * ratio}px ${getComputedStyle(element).fontFamily || 'sans-serif'}`;
  context.textBaseline = 'middle';
  context.fillText(meta.header, padding, padding + lineHeight / 2, picture.width);
  context.drawImage(picture, padding, padding + lineHeight);
  context.fillText(meta.footer, padding, padding + lineHeight + picture.height + lineHeight / 2, picture.width);

  const previewUrl = canvas.toDataURL('image/png');
  const blob = await (await fetch(previewUrl)).blob();
  return { previewUrl, blob, width: canvas.width / ratio, height: canvas.height / ratio };
}

/**
 * Highcharts draws its charts as inline SVG. Cloning those SVGs together with their computed styles
 * can give wrong output (e.g. a black area). So each chart is drawn onto a canvas with white
 * background by hand and replaced by that picture for the time of the capture. Returns the function
 * that puts the original charts back.
 */
async function replaceChartsWithImages(root: HTMLElement): Promise<() => void> {
  const restoreSteps: (() => void)[] = [];
  const svgs = Array.from(root.querySelectorAll<SVGSVGElement>('.highcharts-container > svg'));
  for (const svg of svgs) {
    const { width, height } = svg.getBoundingClientRect();
    if (width === 0 || height === 0) {
      continue;
    }
    try {
      const pictureUrl = await rasterizeSvg(svg, width, height);
      const image = document.createElement('img');
      image.src = pictureUrl;
      image.style.cssText = `display:block;width:${width}px;height:${height}px;`;
      await image.decode();
      svg.parentNode?.insertBefore(image, svg);
      svg.style.display = 'none';
      restoreSteps.push(() => {
        image.remove();
        svg.style.display = '';
      });
    } catch {
      // keep the original chart if it cannot be turned into a picture
    }
  }
  return () => restoreSteps.forEach(step => step());
}

/** Style properties that decide how SVG shapes and texts look. */
const SVG_STYLE_PROPERTIES = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'opacity',
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'letter-spacing',
  'text-anchor',
  'dominant-baseline',
  'visibility',
];

/**
 * Highcharts runs in "styled mode": the SVG elements only carry class names, the colors and fonts
 * come from the stylesheets of the page. A SVG that is rendered as a standalone picture does not
 * have those stylesheets (shapes would be black). So the computed styles of every element are
 * written into the copy as inline styles. Original and copy have the same structure.
 */
function inlineComputedStyles(original: SVGSVGElement, copy: SVGSVGElement): void {
  const originalElements = [original, ...Array.from(original.querySelectorAll('*'))];
  const copiedElements = [copy, ...Array.from(copy.querySelectorAll('*'))];
  originalElements.forEach((element, index) => {
    const target = copiedElements[index] as SVGElement | undefined;
    if (!target) {
      return;
    }
    const computed = getComputedStyle(element);
    SVG_STYLE_PROPERTIES.forEach(property => target.style.setProperty(property, computed.getPropertyValue(property)));
  });
}

/** Draws the SVG onto a white canvas (at the capture's pixel ratio) and returns it as PNG data URL. */
async function rasterizeSvg(svg: SVGSVGElement, width: number, height: number): Promise<string> {
  const copy = svg.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svg, copy);
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('width', String(width));
  copy.setAttribute('height', String(height));
  const source = new Image();
  source.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(copy))}`;
  await source.decode();

  const ratio = CAPTURE_OPTIONS.pixelRatio;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas not available');
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

/**
 * Opens the browser's print dialog (destination, pages, format; "Save as PDF" is the PDF export)
 * for the given picture. The picture is printed from a hidden iframe, so the app itself needs no
 * print styles. The iframe title becomes the suggested file name. Resolves when the dialog is closed.
 * The browser does not tell whether the user saved or cancelled there.
 */
export function printImage(imageUrl: string, title: string, fit?: PrintFit): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
    // Chrome/Edge suggest the title of the main page as file name, not the one of the iframe. So the
    // page title is set to the file name for the time of printing and put back afterwards.
    const originalTitle = document.title;
    document.title = title;
    const cleanup = () =>
      window.setTimeout(() => {
        document.title = originalTitle;
        frame.remove();
      }, 0);

    frame.onload = () => {
      const printWindow = frame.contentWindow;
      if (!printWindow) {
        cleanup();
        reject(new Error('Der Druckdialog konnte nicht geöffnet werden'));
        return;
      }
      const image = printWindow.document.querySelector('img');
      const startPrint = () => {
        let done = false;
        const finish = () => {
          if (!done) {
            done = true;
            cleanup();
            resolve();
          }
        };
        printWindow.addEventListener('afterprint', finish);
        printWindow.focus();
        printWindow.print();
        // Some browsers block in print() until the dialog is closed and fire no afterprint.
        finish();
      };
      if (image && !image.complete) {
        image.onload = startPrint;
        image.onerror = () => {
          cleanup();
          reject(new Error('Das Bild konnte nicht gedruckt werden'));
        };
      } else {
        startPrint();
      }
    };

    frame.srcdoc =
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
      // Page margin 0 makes the browser leave out its header/footer (date, title, URL, page number),
      // they are printed into the page margin.
      printStyles(fit) +
      `</head><body><img src="${imageUrl}" alt=""></body></html>`;
    document.body.appendChild(frame);
  });
}

/** Size in CSS pixels of the picture. With it the print is scaled to fit onto exactly one page. */
export type PrintFit = Pick<CapturedImage, 'width' | 'height'>;

/**
 * Print styles. Without `fit` the picture fills the page width and may continue on further pages
 * (whole dashboard). With `fit` (single tile) the picture is scaled down to fit onto one page, in the
 * orientation the user chooses in the print dialog.
 */
function printStyles(fit?: PrintFit): string {
  if (!fit) {
    return '<style>@page{size:A4 landscape;margin:0}html,body{margin:0}img{display:block;width:100%;height:auto}</style>';
  }
  // No page size/orientation is set: the user picks it in the print dialog. While printing, the
  // viewport of the iframe is the printable page, so vw/vh scale the picture onto that page in
  // either orientation. 1vh less than the page, so rounding never pushes it onto a second page.
  return (
    '<style>@page{margin:0}' +
    'html,body{margin:0;overflow:hidden}' +
    'body{width:100vw;height:99vh;display:flex;align-items:flex-start;justify-content:center}' +
    'img{display:block;max-width:100vw;max-height:99vh;width:auto;height:auto;object-fit:contain}</style>'
  );
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Copies a PNG into the clipboard. Has to be called within a click handler. */
export function copyPngToClipboard(blob: Blob): Promise<void> {
  return navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
}

/** Waits until the browser has rendered the pending DOM changes. */
export function nextRender(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

export const EXPORT_LOGO_URL = 'https://cdn.dam.union-investment.de/original/454499_UI_Logo_RGB.svg';

/**
 * Loads an image and returns it as data URL, so it is embedded in the capture and does not depend
 * on CORS during rendering. Returns null if it cannot be loaded (e.g. the CDN sends no CORS header),
 * the caller then shows a text fallback.
 */
export async function loadImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
